// Utility: fetchWithRetry
// Simple exponential backoff (ignore Retry-After). Defaults:
// - maxRetries: 3 attempts after the initial try
// - Backoff: fixed 300ms * 2^(attempt-1), capped at 10s (no jitter)
// - retry conditions: network errors and HTTP 429/500/502/503/504
// - only idempotent methods (GET/HEAD/OPTIONS) are retried by default

export type FetchRetryOptions = {
	maxRetries?: number;
	retryOnHttpStatus?: number[];
	retryOnNetworkError?: boolean;
	retryMethods?: string[];
	onRetry?: (attempt: number, reason: { error?: any; response?: Response }, delayMs: number) => void;
};

const DEFAULT_RETRY_STATUSES = [429, 500, 502, 503, 504];
const DEFAULT_RETRY_METHODS = ["GET", "HEAD", "OPTIONS"]; // Idempotent by default

function computeDelay(attempt: number): number {
	// Fixed exponential backoff: 300ms, 600ms, 1200ms, ... capped at 10s
	const base = 300;
	const factor = 2;
	const cap = 10_000;
	const raw = base * Math.pow(factor, Math.max(0, attempt - 1));
	return Math.min(cap, Math.floor(raw));
}

function shouldRetry(method: string, resOrErr: Response | { error: any }, retryStatuses: number[], retryOnNetworkError: boolean, retryMethods: string[]): boolean {
	const isMethodAllowed = retryMethods.includes(method.toUpperCase());
	if (!isMethodAllowed) return false;
	if ("error" in resOrErr) return retryOnNetworkError;
	const res = resOrErr as Response;
	return retryStatuses.includes(res.status);
}

export async function fetchWithRetry(input: RequestInfo | URL, init: RequestInit = {}, options: FetchRetryOptions = {}): Promise<Response> {
	const {
		maxRetries = 3,
		retryOnHttpStatus = DEFAULT_RETRY_STATUSES,
		retryOnNetworkError = true,
		retryMethods = DEFAULT_RETRY_METHODS,
		onRetry,
	} = options;

	const method = (init.method || (input instanceof Request ? input.method : "GET")).toUpperCase();

	let lastError: any = null;
	for (let attempt = 0; attempt <= maxRetries; attempt++) {
		try {
			const res = await fetch(input as any, init);
			const retry = shouldRetry(method, res, retryOnHttpStatus, retryOnNetworkError, retryMethods);
			if (!retry || attempt === maxRetries) {
				return res;
			}
			const delay = computeDelay(attempt + 1);
			onRetry?.(attempt + 1, { response: res }, delay);
			await new Promise((r) => setTimeout(r, delay));
		} catch (err: any) {
			lastError = err;
			const retry = shouldRetry(method, { error: err }, retryOnHttpStatus, retryOnNetworkError, retryMethods);
			if (!retry || attempt === maxRetries) {
				throw err;
			}
			const delay = computeDelay(attempt + 1);
			onRetry?.(attempt + 1, { error: err }, delay);
			await new Promise((r) => setTimeout(r, delay));
		}
	}
	if (lastError) throw lastError;
	return fetch(input as any, init);
}

export default fetchWithRetry;

